import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Store
import { RootState, Dispatch } from '../../store/store';
import { Contract } from '../../store/models/contracts';

// Components
import Table from '../../components/Table/Table';
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import IconInputOne from '../../components/Icons/IconInputOne/IconInputOne';
import IconEditOutlined from '../../components/Icons/IconEditOutlined/IconEditOutlined';
import IconBin from '../../components/Icons/IconBin/IconBin';
import Modal from '../../components/Modal/Modal';

// Enums
import StatusLocationContracts from '../../enums/status-contracts-location.enum';

// Types
import { IActionsDropdown, IActionsTag } from '../../types/tableDropdown';
import { ElementSize } from '../../types/element-size';

// Constants
import ROUTE from '../../constants/route-constants';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import ButtonGroups, { ButtonGroupAction } from '../../components/ButtonGroup/ButtonGroup';
import { DateFormatter } from '../../utils/date-formatter';
import { useTitle } from '../../hooks/useTitle';
import { LocationContract } from '../../store/models/locations';
import { useAbilityContext } from '../../components/Can/Can';
import { Action } from '../../enums/action';
import { PAGE_TITLE } from '../../constants/common';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../components/Tooltip';
import { ButtonType } from '../../types/button-type';
import Delimiter from '../../components/Delimiter/Delimiter';
import { DecimeterPosition } from '../../components/Delimiter/enums/position.enum';

type LocationContractsViewProps = ReturnType<typeof mapDispatch> & ReturnType<typeof mapState>;

const LocationContractsView: React.FC<LocationContractsViewProps> = ({
  getLocation,
  getContractsByLocation,
  deleteContract,
  location,
  contracts,
  resetLocations,
  resetContracts,
}) => {
  const SEARCH_QUERY = {
    STATUS: 'status',
  };
  const DEFAULT_SEARCH_QUERY: StatusLocationContracts = StatusLocationContracts.OPEN;

  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const pathToViewOverview = generatePath(ROUTE.LOCATION_CONTRACTS_OVERVIEW, { clientId });

  const firstLoadStatus = (searchParams.get(SEARCH_QUERY.STATUS) || DEFAULT_SEARCH_QUERY) as StatusLocationContracts;

  const ability = useAbilityContext()

  const canCreateContract = useMemo(() => {
    return ability.can(Action.Read, PAGE_TITLE.CREATE_CONTRACT)
  }, [ability])

  const canViewOverview = useMemo(() => {
    return ability.can(Action.Read, PAGE_TITLE.OVERVIEW)
  }, [ability])

  const canEditContract = useMemo(() => {
    return ability.can(Action.Read, PAGE_TITLE.EDIT_CONTRACT)
  }, [])

  //TODO
  //Create text variable for deletion
  const canDeleteContract = useMemo(() => {
    return ability.can(Action.Read, PAGE_TITLE.CREATE_CONTRACT)
  }, [])

  const [isLoadingContracts, setIsLoadingContracts] = useState(true);
  const [contractToDelete, setContractToDelete] = useState<null | Contract>(null);
  const [status, setStatus] = useState<StatusLocationContracts>(firstLoadStatus)

  const contractsView = contracts.map((contract) => ({
    Name: contract.name,
    'Start Date': DateFormatter({ date: contract.start_at }),
    'End Date': contract.endless ? 'Endless' : DateFormatter({ date: contract.start_at }),
    Client: location?.name,
    locationId: location?.id,
    id: contract.id,
  }));

  const goToContractBtn: IActionsDropdown = {
    icon: () => (
      <Tooltip hint={t('commonText.openContracts')} >
        <IconInputOne size={ElementSize.XXS} />
      </Tooltip>
    ),
    onClick: (contractId: string, payload: Contract) => {
      const path = generatePath(ROUTE.LOCATION_CONTRACT_SCREENS, { clientId: payload.locationId, contractId });
      window.open(path, '_blank');
    },
    loading: false,
    tag: IActionsTag.Link,
  }

  const editContractBtn = {
    icon: () => (
      <Tooltip hint={t('commonText.edit')} >
        <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />
      </Tooltip>
    ),
    onClick: (contractId: string) => {
      const path = generatePath(ROUTE.EDIT_CONTRACT, { id: contractId });
      navigate(path);
    },
    loading: false,
    tag: IActionsTag.Link,
  }

  const deleteContractBtn: IActionsDropdown = {
    icon: () => (
      <Tooltip hint={t('commonText.delete')} hintClassName='right-[-90%] bottom-7' isCustomSettings>
        <IconBin fill="#000" size={ElementSize.XXS} />
      </Tooltip>
    ),
    onClick: async (...args) => {
      openModalDeletion(args[1])
    },
    loading: false,
  }

  const actionsButtons: IActionsDropdown[] = useMemo(() => {
    const buttons: IActionsDropdown[] = [goToContractBtn]

    if (canEditContract) {
      buttons.push(editContractBtn)
    }
    if (canDeleteContract) {
      buttons.push(deleteContractBtn)
    }
    return buttons
  }, [canEditContract, canDeleteContract])

  const updateStatus = (state: StatusLocationContracts) => {
    setSearchParams({ [SEARCH_QUERY.STATUS]: state });
    setStatus(state);
  }

  const fetchLocationData = async (id: string) => {
    try {
      await getLocation(id);
    } catch {
      // Supervisors can now see every client in the list, but can only open
      // ones they're assigned to - the backend 403s otherwise, so bounce
      // out instead of leaving the page spinning forever.
      navigate(ROUTE.FORBIDDEN_RESOURCE);
    }
  };

  const fetchContractsByLocation = async (locationId: string, status: string | null) => {
    fetchLocationData(clientId);
    const args: { locationId: string, query?: { [key: string]: string } } = { locationId };

    if (status) {
      args.query = {
        [SEARCH_QUERY.STATUS]: status
      }
    }

    await getContractsByLocation(args);
    setIsLoadingContracts(false);
  };

  const getContractsLengthByStatus = (contracts: LocationContract[] | null, status: StatusLocationContracts) => {
    if (!contracts) {
      return null;
    }

    if (status === StatusLocationContracts.OPEN) {
      const currentTime = new Date().getTime();
      const length = contracts.filter((contract) => contract.endless || new Date(contract.end_at).getTime() > currentTime).length;
      return length;
    }

    if (status === StatusLocationContracts.CLOSED) {
      const currentTime = new Date().getTime();
      const length = contracts.filter((contract) => contract.endless === false && new Date(contract.end_at).getTime() < currentTime).length;
      return length;
    }

    return null
  };

  const ACTIONS_BUTTONS: ButtonGroupAction[] = [
    {
      id: 'open',
      text: `Open (${getContractsLengthByStatus(location?.contracts, StatusLocationContracts.OPEN)})`,
      onClick: () => getOpenedContracts(),
      type: 'button',
      is_active: (searchParams.get(SEARCH_QUERY.STATUS) === StatusLocationContracts.OPEN),
    },
    {
      id: 'close',
      text: `Closed (${getContractsLengthByStatus(location?.contracts, StatusLocationContracts.CLOSED)})`,
      onClick: () => getClosedContracts(),
      type: 'button',
      is_active: (searchParams.get(SEARCH_QUERY.STATUS) === StatusLocationContracts.CLOSED),
    },
  ];

  const getClosedContracts = () => {
    setIsLoadingContracts(true);
    updateStatus(StatusLocationContracts.CLOSED);
  }

  const getOpenedContracts = () => {
    setIsLoadingContracts(true);
    updateStatus(StatusLocationContracts.OPEN);
  }

  const removeContract = async (contractId: string, locationId: string, status: string | null) => {
    await deleteContract(contractId);
    await fetchContractsByLocation(locationId, status);
  };

  const acceptDeleteContract = () => {
    fetchLocationData(clientId);

    const status = searchParams.get(SEARCH_QUERY.STATUS);
    removeContract(contractToDelete.id, clientId, status);

    closeModalDeletion();
  }

  const openModalDeletion = (contract) => {
    setContractToDelete(contract);
  }

  const closeModalDeletion = () => {
    setContractToDelete(null)
  }

  const moveCreateContract = () => {
    navigate(ROUTE.CREATE_CONTRACT);
  };

  useEffect(() => {
    return () => {
      resetContracts();
      resetLocations();
    }
  }, []);

  useEffect(() => {
    setSearchParams({ [SEARCH_QUERY.STATUS]: status });
    fetchContractsByLocation(clientId, status);
  }, [status]);

  const title = location?.name ? `${location?.name} contracts` : 'undefined';

  useTitle(title);

  return (
    <>
      {(!location) && <div className="h-full w-full flex items-center justify-center">
        <SpinnerLoader />
      </div>}
      {(location) && (
        <>
          <div className='p-location-contracts-view flex flex-col h-full'>
            <h2 className="text-title-md2 pb-5 font-semibold text-black dark:text-white">
              {title}
            </h2>
            <div className='flex max-xsm:flex-col pb-3 gap-x-4 gap-y-2'>
              <ButtonGroups className='min-xsm:max-h-9 max-xsm:grid-rows-2 gap-y-2' actions={ACTIONS_BUTTONS} />
              <Delimiter className='w-0.5 bg-foggy-mist max-xsm:w-full max-xsm:h-[1px]' position={DecimeterPosition.Vertical} />
              <div className='flex flex-auto flex-row justify-between gap-x-4'>
                {canViewOverview && (
                  <InteractiveButton
                    className='bg-white max-xsm:w-full'
                    type={ButtonType.PrimaryInverse}
                    text="Overview"
                    as="a"
                    href={pathToViewOverview}
                    target="_blank"
                  />
                )}
                {canCreateContract &&
                  <InteractiveButton className='max-xsm:w-full' text="Create" onClick={moveCreateContract} />
                }
              </div>
            </div>
            {isLoadingContracts && (<div className="h-full w-full flex items-center justify-center">
              <SpinnerLoader />
            </div>)}
            {!isLoadingContracts && (<Table
              values={contractsView}
              defaultKeys={['Name', 'Start Date', 'End Date', 'Client']}
              actionsButtons={actionsButtons}
            />)}
          </div>
          <Modal
            modalTitle="Contract deletion"
            isVisible={contractToDelete !== null}
            content={`Are you sure you want to delete the ${contractToDelete ? contractToDelete['Name'] : ''} contract?`}
            acceptBtnText="Delete"
            onClose={closeModalDeletion}
            onAccept={acceptDeleteContract}
            showAcceptBtn
            showCancelBtn />
        </>
      )}
    </>
  )
};

const mapState = (state: RootState) => ({
  location: state.locations.location,
  contracts: state.contracts.contracts,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getLocation: dispatch.locations.getLocation,
  getContractsByLocation: dispatch.contracts.getContractsByLocation,
  deleteContract: dispatch.contracts.deleteContract,
  resetLocations: dispatch.locations.resetState,
  resetContracts: dispatch.contracts.resetState,
});

export default connect(mapState, mapDispatch)(LocationContractsView);
